import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ShieldCheck, Lock, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScreeningConsentPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    dob: "", ssn_last4: "", current_address: "",
    monthly_income: "", employer: "", employment_years: "",
    consent_signed_name: "", consent_agreement: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    async function fetchInfo() {
      try {
        const { data } = await axios.get(`${API}/screening/token/${token}`);
        setInfo(data);
        if (data.already_submitted) setSubmitted(true);
      } catch (e) {
        setError(e?.response?.data?.detail || "This screening link is invalid or expired.");
      }
      setLoading(false);
    }
    fetchInfo();
  }, [token]);

  const submit = async () => {
    if (!form.consent_agreement) return toast.error("You must agree to the FCRA disclosure");
    if (!form.dob || !form.ssn_last4 || !form.current_address || !form.monthly_income || !form.consent_signed_name) {
      return toast.error("Please fill in all required fields");
    }
    setBusy(true);
    try {
      await axios.post(`${API}/screening/token/${token}/submit`, {
        ...form,
        monthly_income: Number(form.monthly_income),
        employment_years: form.employment_years ? Number(form.employment_years) : null,
      });
      setSubmitted(true);
      toast.success("Consent submitted. You'll hear back soon.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    }
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-zinc-200 flex items-center justify-center">
        <div className="text-sm text-zinc-500">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-zinc-200 flex items-center justify-center px-4">
        <Card className="border-white/10 bg-card p-8 max-w-md text-center" data-testid="screening-error">
          <div className="text-2xl font-semibold mb-2">Link Unavailable</div>
          <div className="text-sm text-zinc-400">{error}</div>
          <div className="text-xs text-zinc-500 mt-4">Please contact the property manager who invited you for a new link.</div>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-zinc-200 flex items-center justify-center px-4">
        <Card className="border-emerald-500/30 bg-emerald-500/[0.05] p-8 max-w-md text-center" data-testid="screening-submitted">
          <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
          <div className="text-2xl font-semibold mb-2">Consent Received</div>
          <div className="text-sm text-zinc-300">
            Thanks {info?.applicant_name}. Your screening is now in review — we'll email you a decision within 1–2 business days.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-zinc-200 py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-orange-400 mb-2">
            <ShieldCheck className="h-4 w-4" /> Secure applicant screening
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold font-display">Complete your rental screening</h1>
          <p className="text-sm text-zinc-500 mt-2">
            For {info?.property_name}
            {info?.unit ? ` — Unit ${info.unit}` : ""}
            {info?.monthly_rent ? ` — $${Number(info.monthly_rent).toLocaleString()}/mo` : ""}
          </p>
        </div>

        <Card className="border-white/10 bg-card p-6" data-testid="screening-consent-form">
          <div className="text-[10px] uppercase tracking-widest text-orange-400 mb-2">Applicant · {info?.applicant_name}</div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date of birth *</Label>
              <Input type="date" value={form.dob}
                     onChange={(e) => setForm({ ...form, dob: e.target.value })}
                     data-testid="consent-dob" />
            </div>
            <div>
              <Label>SSN (last 4 only) *</Label>
              <Input maxLength={4} inputMode="numeric"
                     value={form.ssn_last4}
                     onChange={(e) => setForm({ ...form, ssn_last4: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                     placeholder="1234" data-testid="consent-ssn" />
              <div className="text-[10px] text-zinc-500 mt-1 flex items-center gap-1">
                <Lock className="h-3 w-3" /> Only last 4 digits retained
              </div>
            </div>
            <div className="col-span-2">
              <Label>Current address *</Label>
              <Input value={form.current_address}
                     onChange={(e) => setForm({ ...form, current_address: e.target.value })}
                     placeholder="123 Main St, Anytown FL 33456" data-testid="consent-address" />
            </div>
            <div>
              <Label>Monthly income (USD) *</Label>
              <Input type="number" value={form.monthly_income}
                     onChange={(e) => setForm({ ...form, monthly_income: e.target.value })}
                     placeholder="6500" data-testid="consent-income" />
            </div>
            <div>
              <Label>Employer</Label>
              <Input value={form.employer}
                     onChange={(e) => setForm({ ...form, employer: e.target.value })}
                     placeholder="Company Inc." data-testid="consent-employer" />
            </div>
            <div className="col-span-2">
              <Label>Years employed</Label>
              <Input type="number" step="0.1" value={form.employment_years}
                     onChange={(e) => setForm({ ...form, employment_years: e.target.value })}
                     placeholder="2.5" data-testid="consent-emp-years" />
            </div>
          </div>

          <div className="mt-6 p-4 rounded-md border border-white/10 bg-white/[0.02] text-xs text-zinc-400 space-y-2">
            <div className="font-semibold text-zinc-200">FCRA Disclosure & Authorization</div>
            <p>
              I authorize Prestigious Property Management to obtain a consumer report about me from a consumer reporting
              agency for tenant screening purposes. This may include credit history, criminal records, eviction filings,
              and rental references. I understand my rights under the Fair Credit Reporting Act (15 U.S.C. § 1681 et seq.).
            </p>
            <p>
              I certify that all information provided is true and complete. I understand that false or misleading
              information may result in the denial of my application or termination of any resulting lease.
            </p>
          </div>

          <div className="mt-4 flex items-start gap-2">
            <Checkbox id="agree" checked={form.consent_agreement}
                      onCheckedChange={(v) => setForm({ ...form, consent_agreement: !!v })}
                      data-testid="consent-agree" />
            <Label htmlFor="agree" className="text-sm cursor-pointer">
              I have read, understood, and agree to the FCRA disclosure above.
            </Label>
          </div>

          <div className="mt-4">
            <Label>Sign your full legal name *</Label>
            <Input value={form.consent_signed_name}
                   onChange={(e) => setForm({ ...form, consent_signed_name: e.target.value })}
                   placeholder="Jane R. Applicant"
                   className="font-serif italic"
                   data-testid="consent-signature" />
          </div>

          <Button onClick={submit}
                  disabled={busy || !form.consent_agreement || !form.dob || !form.ssn_last4 || !form.current_address || !form.monthly_income || !form.consent_signed_name}
                  className="mt-6 w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="consent-submit-btn">
            {busy ? "Submitting…" : !form.consent_agreement ? "Agree to disclosure to continue" : "Submit consent & screening authorization"}
          </Button>

          <div className="mt-4 text-[10px] text-zinc-500 text-center flex items-center justify-center gap-2">
            <Lock className="h-3 w-3" /> Encrypted in transit. Full SSN never stored. Report retained per FCRA compliance policy.
          </div>
        </Card>
      </div>
    </div>
  );
}
